import { spaceObjectFromBack } from '@entities/responseFormatters';
import { SpaceObjectRequest } from '@entities/SpaceObject/schemas';
import { SpaceObjectBack, SpaceObjectFront } from '@entities/SpaceObject/type';
import { convertFiltersToApiParams } from '@features/ui-filters/api/filterUtils';

import { PaginatedOptions, PaginatedResponse } from '@root/src/types/common';
import { mapPaginatedResponse } from '@root/src/utils/formatters/responseAdapters';
import { fetchRequest, RequestMethod } from '@services/api/utils/utils';

/**
 * Get space objects with filters
 * @example
 * // Get all space objects related to a user (as creator, operator, or via member)
 * getSpaceObjects({
 *   filters: {
 *     _id: user.spaceObjects, // Direct space objects array from user
 *     user: userId,           // Space objects where user is creator
 *     operator: [userId],     // Space objects where user is operator
 *     owner: memberId,        // Space objects owned by user's member
 *   }
 * })
 *
 * // Get space objects created by a user
 * getSpaceObjects({ filters: { user: "userId" } })
 *
 * // Get space objects owned by a member
 * getSpaceObjects({ filters: { owner: "memberId" } })
 *
 * // Get space objects with multiple operators
 * getSpaceObjects({ filters: { operator: ["operator1Id", "operator2Id"] } })
 */
type SpaceObjectsPaginatedOptions = PaginatedOptions & {
  memberContext?: string;
};

const getSpaceObjects = async ({
  filters = {},
  page = 1,
  pageSize = 15,
  sort = { field: 'name', order: 'ASC' },
  memberContext,
}: SpaceObjectsPaginatedOptions = {}): Promise<PaginatedResponse<SpaceObjectFront>> =>
  fetchRequest<PaginatedResponse<SpaceObjectBack>, PaginatedResponse<SpaceObjectFront>>({
    endpoint: '/spaceobjects',
    method: RequestMethod.GET,
    query: convertFiltersToApiParams(filters, {
      page,
      pageSize,
      memberContext,
      sorted: [
        {
          id: sort.field,
          desc: sort.order,
        },
      ],
    }),
    responseFormatter: data => mapPaginatedResponse(data, spaceObjectFromBack),
  });

const getSpaceObjectById = async (id: string): Promise<SpaceObjectFront> =>
  fetchRequest<SpaceObjectBack, SpaceObjectFront>({
    endpoint: `/spaceobjects/${id}`,
    method: RequestMethod.GET,
    responseFormatter: spaceObjectFromBack,
  });

/** The body is the form's parsed payload as it is: every owned field, a value or `null`. */
const createSpaceObject = async (spaceObject: SpaceObjectRequest): Promise<SpaceObjectFront> =>
  fetchRequest<SpaceObjectBack, SpaceObjectFront, SpaceObjectRequest>({
    endpoint: '/spaceobjects',
    method: RequestMethod.POST,
    body: spaceObject,
    responseFormatter: spaceObjectFromBack,
  });

const updateSpaceObject = async (spaceObject: SpaceObjectRequest, id: string): Promise<SpaceObjectFront> =>
  fetchRequest<SpaceObjectBack, SpaceObjectFront, SpaceObjectRequest>({
    endpoint: `/spaceobjects/${id}`,
    method: RequestMethod.PUT,
    body: spaceObject,
    responseFormatter: spaceObjectFromBack,
  });

const deleteSpaceObject = async (id: string): Promise<SpaceObjectFront> =>
  fetchRequest<SpaceObjectBack, SpaceObjectFront, SpaceObjectBack>({
    endpoint: `/spaceobjects/${id}`,
    method: RequestMethod.DELETE,
  });

type BulkEditFields = {
  pointOfContact: string | null;
};

type BulkEditResponse = {
  total: number;
  updated: number;
  failed: number;
  notFound: number;
  errors: string[];
};

const BULK_EDIT_MAX_IDS = 5000;

const bulkEditSpaceObjects = async (ids: string[], fields: BulkEditFields): Promise<BulkEditResponse> => {
  if (ids.length <= BULK_EDIT_MAX_IDS) {
    return fetchRequest<BulkEditResponse, BulkEditResponse, { ids: string[]; fields: BulkEditFields }>({
      endpoint: '/spaceobjects/bulk-edit',
      method: RequestMethod.PUT,
      body: { ids, fields },
    });
  }

  const aggregated: BulkEditResponse = { total: 0, updated: 0, failed: 0, notFound: 0, errors: [] };
  for (let i = 0; i < ids.length; i += BULK_EDIT_MAX_IDS) {
    const batch = ids.slice(i, i + BULK_EDIT_MAX_IDS);
    const result = await fetchRequest<BulkEditResponse, BulkEditResponse, { ids: string[]; fields: BulkEditFields }>({
      endpoint: '/spaceobjects/bulk-edit',
      method: RequestMethod.PUT,
      body: { ids: batch, fields },
    });
    aggregated.total += result.total;
    aggregated.updated += result.updated;
    aggregated.failed += result.failed;
    aggregated.notFound += result.notFound;
    aggregated.errors.push(...result.errors);
  }
  return aggregated;
};

export const spaceObjectsApi = {
  getSpaceObjects,
  getSpaceObjectById,
  createSpaceObject,
  updateSpaceObject,
  deleteSpaceObject,
  bulkEditSpaceObjects,
};
